// Shared query config + variable builders for the TWG recs query. Used by both the
// client hook and the SSR preloader so they issue byte-identical variables (needed
// for the client's cache-first read to hit the SSR-primed entry).

// TWG relationship for related Confluence content (spec: CA3).
const TWG_RELATIONSHIP = 'confluence_content_has_related_confluence_content';

// Relationship from a content node to its authority-score node.
const TWG_AUTHORITY_RELATIONSHIP = 'confluence_content_has_confluence_content_authority_score';

// Buffer above the 6-card grid limit so permission-filtered nodes don't drop us
// below the "min 2 cards" render invariant.
export const MAX_TWG_RECS = 12;

// AGG needs an `X-Query-Context` site-ARI header for `graphStore_*` fields.
export const buildTwgQueryContext = (cloudId: string): string =>
	`ari:cloud:platform::site/${cloudId}`;

// Matches @atlassian/ari's Confluence{Page,BlogPost}Ari, inlined to avoid the
// dependency. Callers guard with isTwgSupportedContentType, so contentType is
// 'page' or 'blogpost'; blogposts need the `blogpost/` segment or the graph lookup
// resolves to a non-existent node.
export const buildTwgContentAri = (
	cloudId: string,
	contentId: string,
	contentType: string,
): string =>
	`ari:cloud:confluence:${cloudId}:${contentType === 'blogpost' ? 'blogpost' : 'page'}/${contentId}`;

// No cypher `LIMIT`: the FE filters non-current content after hydration (see
// `extractTwgRecContent`), so limiting here would only shrink that pool; `first` still
// bounds the edges. Each row returns `entity` (the related content node) and `auth`
// (its authority-score node, if any), which the FE uses to rank results.
export const buildTwgRecsCypher = (pageAri: string): string =>
	[
		`MATCH (page {ari: "${pageAri}"})-[:${TWG_RELATIONSHIP}]->(entity)`,
		`OPTIONAL MATCH (entity)-[:${TWG_AUTHORITY_RELATIONSHIP}]->(auth)`,
		`RETURN DISTINCT entity, auth`,
	].join('\n');

/** Content types the TWG recommendations query supports. */
export const isTwgSupportedContentType = (contentType: string | undefined): boolean =>
	contentType === 'page' || contentType === 'blogpost';
